import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DateRangePicker, { DateRange, defaultRange } from './DateRangePicker';
import KpiCards from './KpiCards';
import TimeSeriesChart from './TimeSeriesChart';

/**
 * /reports/bodies/:id — single data body drilldown.
 *
 * Layout:
 *   - Back link + body name header
 *   - Date range picker (same as overview, persisted same way)
 *   - Body-level KPI cards (brain accept/reject only)
 *   - Body time series chart
 *   - Vertebrae table (every spine node that saw traffic in the range)
 *   - Legs (each leg is its own card with the leg-level totals at top
 *     and its leg_nodes listed underneath, grouped by leg)
 *
 * The accept/reject numbers here come from the same rollup tables the
 * lifetime counters on the builder UI use, just sliced to the date
 * range. They reconcile: sum a vertebra's daily accepts over all time
 * and you get its pass_count.
 */

function fmt(n: number): string {
  return n.toLocaleString();
}
function fmtPct(p: number): string {
  return (p * 100).toFixed(1) + '%';
}

export default function ReportingBodyPage() {
  const { id } = useParams<{ id: string }>();
  const { organization } = useAuth();
  const tz = organization?.timezone || undefined;
  const [range, setRange] = useState<DateRange>(() => defaultRange(tz));
  const [body, setBody] = useState<api.ReportingBodyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bodyName, setBodyName] = useState<string | null>(null);

  const load = useCallback(async (forceFresh = false) => {
    if (!id) {return;}
    setLoading(true);
    setError(null);
    try {
      const data = await api.getReportingBody(id, range.from, range.to, forceFresh);
      setBody(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load body report');
    } finally {
      setLoading(false);
    }
  }, [id, range.from, range.to]);

  useEffect(() => { void load(false); }, [load]);

  // Resolve body name once for the header. Falls back gracefully to the id.
  useEffect(() => {
    if (!id) {return;}
    let cancelled = false;
    api.getDataBody(id).then(db => { if (!cancelled) {setBodyName(db.name);} }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [id]);

  // Split node breakdown into vertebrae / legs / leg_nodes for rendering.
  const { vertebrae, legs, legNodesByLeg } = useMemo(() => {
    const nodes = body?.nodes ?? [];
    const vertebrae = nodes.filter(n => n.nodeKind === 'vertebra');
    const legs = nodes.filter(n => n.nodeKind === 'leg');
    const legNodes = nodes.filter(n => n.nodeKind === 'leg_node');
    const grouped = new Map<string | null, api.ReportingNodeRow[]>();
    for (const ln of legNodes) {
      const key = ln.legId ?? null;
      const arr = grouped.get(key) ?? [];
      arr.push(ln);
      grouped.set(key, arr);
    }
    return { vertebrae, legs, legNodesByLeg: grouped };
  }, [body]);

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1280, margin: '0 auto' }}>
      <Link
        to="/reports"
        style={{ fontSize: 13, color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}
      >
        ← Back to Reporting
      </Link>
      <h1 style={{ marginTop: 6, marginBottom: 4, fontSize: 28, fontWeight: 700, color: '#111827' }}>
        {bodyName || <span style={{ color: '#9ca3af' }}>Loading body…</span>}
      </h1>
      <p style={{ marginTop: 0, marginBottom: 20, color: '#6b7280', fontSize: 14 }}>
        Brain, spine, and legs — accepts and rejects for every node that saw traffic in the selected range.
      </p>

      <div style={{ marginBottom: 20 }}>
        <DateRangePicker
          value={range}
          onChange={setRange}
          onRefresh={() => load(true)}
          timezone={tz}
        />
      </div>

      {error && (
        <div style={{
          padding: 16,
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
          color: '#991b1b',
          marginBottom: 20,
        }}>
          {error}
        </div>
      )}

      {loading && !body && (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
      )}

      {body && (
        <>
          <div style={{ marginBottom: 24 }}>
            <KpiCards totals={body.overview} />
          </div>

          <DoorRejectsPanel rows={body.doorRejects ?? []} />

          <SectionTitle>Brain volume over time</SectionTitle>
          <div style={{ marginBottom: 28 }}>
            <TimeSeriesChart data={body.series} tz={tz} />
          </div>

          <SectionTitle>Spine vertebrae</SectionTitle>
          <NodeTable
            nodes={vertebrae}
            emptyLabel="No vertebra activity in this range."
            typeLabel="Type"
          />

          <SectionTitle style={{ marginTop: 32 }}>Legs (decision tree)</SectionTitle>
          {legs.length === 0 ? (
            <div style={{
              padding: 24,
              background: '#f9fafb',
              border: '1px dashed #e5e7eb',
              borderRadius: 8,
              color: '#9ca3af',
              textAlign: 'center',
            }}>
              No leg activity in this range.
            </div>
          ) : (
            legs.map(leg => (
              <LegCard
                key={leg.nodeId}
                leg={leg}
                nodes={legNodesByLeg.get(leg.nodeId) ?? []}
              />
            ))
          )}
          {/* Orphan leg_nodes (no parent leg captured) — shouldn't happen
              in practice but render them under "Other" rather than dropping. */}
          {(legNodesByLeg.get(null)?.length ?? 0) > 0 && (
            <LegCard
              leg={{
                nodeKind: 'leg',
                nodeId: 'orphan',
                nodeName: 'Other leg nodes',
                nodeType: null,
                legId: null,
                position: null,
                accepts: 0,
                rejects: 0,
                acceptRate: 0,
              }}
              nodes={legNodesByLeg.get(null) ?? []}
            />
          )}
        </>
      )}
    </div>
  );
}

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <h2 style={{
      margin: '0 0 12px 0',
      fontSize: 16,
      fontWeight: 600,
      color: '#374151',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      ...style,
    }}>
      {children}
    </h2>
  );
}

/**
 * "Blocked at the door" — decomposes the body's brain reject total by reason
 * so a reject spike is a glance, not an investigation. Renders nothing when
 * the body had zero door rejects in the range.
 */
const DOOR_REJECT_LABELS: Record<string, { label: string; hint: string }> = {
  rate_limit: { label: 'Rate limit', hint: 'Source sent faster than this body’s per-second cap' },
  concurrency: { label: 'Concurrency limit', hint: 'Too many simultaneous connections from the source' },
  required_field: { label: 'Missing required field', hint: 'Payload was missing a field marked required' },
  '': { label: 'Unclassified', hint: 'Recorded before reason tracking, or rebuilt from history' },
};

function DoorRejectsPanel({ rows }: { rows: api.ReportingDoorRejectRow[] }) {
  const total = rows.reduce((s, r) => s + r.rejects, 0);
  if (total === 0) {return null;}
  return (
    <div style={{ marginBottom: 28 }}>
      <SectionTitle>Blocked at the door</SectionTitle>
      <div style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          borderBottom: '1px solid #fecaca',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}>
          <span style={{ fontSize: 13, color: '#991b1b', fontWeight: 600 }}>
            {fmt(total)} lead{total === 1 ? '' : 's'} rejected before the spine
          </span>
          <span style={{ fontSize: 12, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: 0.5 }}>by reason</span>
        </div>
        {rows.map((r) => {
          const meta = DOOR_REJECT_LABELS[r.reason] ?? { label: r.reason || 'Unknown', hint: '' };
          const share = total === 0 ? 0 : r.rejects / total;
          return (
            <div key={r.reason || 'unclassified'} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px 80px',
              padding: '12px 16px',
              borderTop: '1px solid #fef2f2',
              alignItems: 'center',
              fontSize: 14,
            }}>
              <div>
                <div style={{ fontWeight: 600, color: '#111827' }}>{meta.label}</div>
                {meta.hint && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{meta.hint}</div>}
              </div>
              <div style={{ textAlign: 'right', color: '#b91c1c', fontWeight: 600 }}>{fmt(r.rejects)}</div>
              <div style={{ textAlign: 'right', color: '#9ca3af' }}>{fmtPct(share)}</div>
            </div>
          );
        })}
      </div>
      <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
        These never created a lead record. Rate-limit and concurrency blocks mean the source exceeded this body’s
        limits — raise them in the body’s settings if the source is trusted.
      </p>
    </div>
  );
}

interface NodeTableProps {
  nodes: api.ReportingNodeRow[];
  emptyLabel: string;
  typeLabel: string;
}

function NodeTable({ nodes, emptyLabel, typeLabel }: NodeTableProps) {
  if (nodes.length === 0) {
    return (
      <div style={{
        padding: 24,
        background: '#f9fafb',
        border: '1px dashed #e5e7eb',
        borderRadius: 8,
        color: '#9ca3af',
        textAlign: 'center',
      }}>
        {emptyLabel}
      </div>
    );
  }
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '40px 1fr 200px 120px 120px 120px',
        padding: '10px 16px',
        background: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
        fontSize: 11,
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}>
        <div>#</div>
        <div>Name</div>
        <div>{typeLabel}</div>
        <div style={{ textAlign: 'right' }}>Accepts</div>
        <div style={{ textAlign: 'right' }}>Rejects</div>
        <div style={{ textAlign: 'right' }}>Rate</div>
      </div>
      {nodes.map((n, i) => (
        <div key={n.nodeId} style={{
          display: 'grid',
          gridTemplateColumns: '40px 1fr 200px 120px 120px 120px',
          padding: '12px 16px',
          borderTop: '1px solid #f3f4f6',
          alignItems: 'center',
          fontSize: 14,
        }}>
          <div style={{ color: '#9ca3af' }}>{n.position ?? i + 1}</div>
          <div style={{ fontWeight: 600, color: '#111827' }}>
            {n.nodeName || <span style={{ color: '#9ca3af' }}>(unnamed)</span>}
          </div>
          <div style={{ color: '#6b7280', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
            {n.nodeType ?? '—'}
          </div>
          <div style={{ textAlign: 'right', color: '#1d4ed8' }}>{fmt(n.accepts)}</div>
          <div style={{ textAlign: 'right', color: '#b91c1c' }}>{fmt(n.rejects)}</div>
          <div style={{ textAlign: 'right', color: '#047857', fontWeight: 600 }}>{fmtPct(n.acceptRate)}</div>
        </div>
      ))}
    </div>
  );
}

interface LegCardProps {
  leg: api.ReportingNodeRow;
  nodes: api.ReportingNodeRow[];
}

function LegCard({ leg, nodes }: LegCardProps) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      marginBottom: 16,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 120px 120px 120px',
        padding: '14px 16px',
        background: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
        alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Leg
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111827', marginTop: 2 }}>
            {leg.nodeName || <span style={{ color: '#9ca3af' }}>(unnamed leg)</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: '#6b7280' }}>Accepts</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1d4ed8' }}>{fmt(leg.accepts)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: '#6b7280' }}>Rejects</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#b91c1c' }}>{fmt(leg.rejects)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: '#6b7280' }}>Rate</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#047857' }}>{fmtPct(leg.acceptRate)}</div>
        </div>
      </div>
      {nodes.length === 0 ? (
        <div style={{ padding: 16, color: '#9ca3af', fontSize: 13 }}>
          No leg-node activity in this range.
        </div>
      ) : (
        nodes.map(n => (
          <div key={n.nodeId} style={{
            display: 'grid',
            gridTemplateColumns: '24px 1fr 180px 120px 120px 120px',
            padding: '12px 16px',
            borderTop: '1px solid #f3f4f6',
            alignItems: 'center',
            fontSize: 14,
          }}>
            <div style={{ color: '#9ca3af' }}>•</div>
            <div style={{ color: '#111827' }}>
              {n.nodeName || <span style={{ color: '#9ca3af' }}>(unnamed)</span>}
            </div>
            <div style={{ color: '#6b7280', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
              {n.nodeType ?? '—'}
            </div>
            <div style={{ textAlign: 'right', color: '#1d4ed8' }}>{fmt(n.accepts)}</div>
            <div style={{ textAlign: 'right', color: '#b91c1c' }}>{fmt(n.rejects)}</div>
            <div style={{ textAlign: 'right', color: '#047857', fontWeight: 600 }}>{fmtPct(n.acceptRate)}</div>
          </div>
        ))
      )}
    </div>
  );
}
